import React, { useState, useEffect, useMemo } from 'react';
import { adminStatsService } from '../services/adminStatsService';
import { supabase, STAT_TYPES, STAT_COLUMNS } from '../services/supabase';
import { useSeasonContext } from '../context/SeasonContext';
import TopPerformersCards from '../components/TopPerformersCards';
import '../styles/WeeklyStats.css';

const getDefaultSortColumn = (statType) => {
  const defaults = {
    'passing': 'QBR',
    'rushing': 'YRDS',
    'receiving': 'YRDS',
    'blocking': 'SKA',
    'defense': 'TCK',
    'kicking': 'PNTS'
  };
  return defaults[statType.toLowerCase()] || null;
};

const WeeklyStats = () => {
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedStatType, setSelectedStatType] = useState('passing');
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [displayStats, setDisplayStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ 
    column: getDefaultSortColumn('passing'), 
    ascending: true 
  });
  
  const { selectedSeason, setSelectedSeason, availableSeasons } = useSeasonContext();

  useEffect(() => {
    if (selectedSeason) {
      loadAvailableWeeks();
    }
  }, [selectedSeason]);

  useEffect(() => {
    if (selectedSeason) {
      loadStatsForWeek();
    }
  }, [selectedWeek, selectedSeason]);

  useEffect(() => {
    setSortConfig({ 
      column: getDefaultSortColumn(selectedStatType), 
      ascending: true 
    });
  }, [selectedStatType]);

  const loadAvailableWeeks = async () => {
    try {
      const weeks = await adminStatsService.getAllWeeksWithData();
      if (Array.isArray(weeks) && weeks.length > 0) {
        setAvailableWeeks(weeks);
        setSelectedWeek(weeks[0]);
      } else {
        setAvailableWeeks([1, 2, 3, 4, 5]);
        setSelectedWeek(1);
      }
    } catch (err) {
      console.error('Error loading weeks:', err);
      setAvailableWeeks([1, 2, 3, 4, 5]);
    }
  };

  const loadStatsForWeek = async () => {
    setLoading(true);
    setError(null);

    try {
      const stats = {};

      for (const statType of Object.values(STAT_TYPES)) {
        const result = await adminStatsService.getStatsForWeekAndSeason(statType, selectedWeek, selectedSeason.id);
        if (result.success) {
          stats[statType.toLowerCase()] = result.data;
        } else {
          stats[statType.toLowerCase()] = [];
        }
      }

      setDisplayStats(stats);
    } catch (err) {
      setError('Failed to load weekly statistics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getColumns = (statType) => {
    const columns = STAT_COLUMNS[statType.toUpperCase()] || [];
    if (statType.toUpperCase() === 'BLOCKING') {
      return [...columns, 'SAD'];
    }
    return columns;
  };

  const currentStatPlayers = useMemo(() => {
    const currentStats = displayStats[selectedStatType] || [];
    return Array.isArray(currentStats) ? currentStats : Object.values(currentStats || {});
  }, [displayStats, selectedStatType]);

  const sortedPlayers = useMemo(() => {
    let sorted = [...currentStatPlayers];
    
    if (sortConfig.column) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.column];
        const bVal = b[sortConfig.column];

        let comparison = 0;
        if (typeof aVal === 'string') {
          comparison = aVal.localeCompare(bVal);
        } else {
          comparison = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0);
        }

        return sortConfig.ascending ? -comparison : comparison;
      });
    }

    return sorted;
  }, [currentStatPlayers, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      ascending: prev.column === column ? !prev.ascending : false,
    }));
  };

  const renderStatValue = (value) => {
    const numValue = parseFloat(value);
    if (numValue > 0) {
      return <span style={{ color: '#4CAF50' }}>{value}</span>;
    } else if (numValue < 0) {
      return <span style={{ color: '#ff6b6b' }}>{value}</span>;
    }
    return value;
  };

  return (
    <div className="page">
      <div className="weekly-stats-leaderboard">
        <div className="header">
          <div className="league-badge">Week Stats</div>
          <h1>Weekly Statistics</h1>
        </div>

        <div className="season-selector-wrapper">
          <div className="season-selector">
            <label htmlFor="season-select">Select Season:</label>
            <select
              id="season-select"
              value={selectedSeason ? String(selectedSeason.id) : ''}
              onChange={(e) => setSelectedSeason(Number(e.target.value))}
            >
              {availableSeasons.map(season => (
                <option key={season.id} value={String(season.id)}>
                  {season.name} {season.is_active ? '(Current)' : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedSeason && (
            <div className="week-selector-inline">
              <label>Select Week:</label>
              <div className="week-buttons-inline">
                {availableWeeks.map(week => (
                  <button
                    key={week}
                    className={`week-btn-inline ${selectedWeek === week ? 'active' : ''}`}
                    onClick={() => setSelectedWeek(week)}
                  >
                    Week {week}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="stat-tabs">
          {['Passing', 'Rushing', 'Receiving', 'Blocking', 'Defense', 'Kicking'].map((label) => (
            <button
              key={label}
              className={`tab-button ${selectedStatType === label.toLowerCase() ? 'active' : ''}`}
              onClick={() => setSelectedStatType(label.toLowerCase())}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading Week {selectedWeek} statistics...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>❌ {error}</p>
            <p className="error-hint">Please try again later</p>
          </div>
        ) : !selectedSeason ? (
          <div className="no-data">
            <p>Please select a season to view statistics</p>
          </div>
        ) : displayStats[selectedStatType]?.length === 0 ? (
          <div className="no-data">
            <p>No data available for {selectedStatType} in Week {selectedWeek}</p>
          </div>
        ) : (
          <>
            <TopPerformersCards stats={displayStats} selectedStatType={selectedStatType} blockingMetric="SAD" />

            <div className="leaderboard-table">
              <h2>Full Leaderboard</h2>
              <div className="stats-table-container">
                <table>
                  <thead>
                    <tr>
                      {getColumns(selectedStatType).map(column => (
                        <th key={column} onClick={() => handleSort(column)}>
                          {column} {sortConfig.column === column && (sortConfig.ascending ? '↑' : '↓')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPlayers.map((player, index) => (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                        {getColumns(selectedStatType).map(column => (
                          <td key={column}>{renderStatValue(player[column] || 0)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WeeklyStats;
